import type { KeyboardDnDHighlight } from "./keyboardDnDTypes";

/**
 * Announcement strings for keyboard move mode (design v2).
 *
 * Contracts:
 * - the LIFT announcement carries the key instructions exactly once;
 * - per-move announcements are neighbor-anchored ("After X, before Y") —
 *   auto-generated zone names and abstract indices are weak anchors on
 *   their own;
 * - dead ends are always voiced (never-silent contract);
 * - drop and cancel are explicitly confirmed.
 */

export function formatLiftAnnouncement(
  widgetName: string,
  zoneName: string,
): string {
  return (
    `Moving ${widgetName} in ${zoneName}. ` +
    "Use arrow keys to choose a position, Tab to switch zones, " +
    "Enter to drop, Escape to cancel."
  );
}

export function formatMoveAnnouncement(
  highlight: KeyboardDnDHighlight,
  index: number,
  total: number,
  zoneName: string,
  zoneChildNames: string[],
  movedWidgetName: string,
): string {
  const insertionIndex = highlight.rowIndex;

  // The zone's children still include the widget being moved — it must not
  // be announced as its own neighbor, so anchors step past it.
  let beforeIndex = insertionIndex;

  while (zoneChildNames[beforeIndex] === movedWidgetName) beforeIndex += 1;

  let afterIndex = insertionIndex - 1;

  while (afterIndex >= 0 && zoneChildNames[afterIndex] === movedWidgetName)
    afterIndex -= 1;

  const before = zoneChildNames[beforeIndex];
  const after = afterIndex >= 0 ? zoneChildNames[afterIndex] : undefined;

  const position = `${index + 1} of ${total}.`;

  // Edge/empty anchors already name the zone — do not repeat it.
  if (after && before) {
    return `After ${after}, before ${before}, in ${zoneName}. ${position}`;
  } else if (before) {
    return `Start of ${zoneName}, before ${before}. ${position}`;
  } else if (after) {
    return `End of ${zoneName}, after ${after}. ${position}`;
  }

  return `In empty ${zoneName}. ${position}`;
}

export function formatDeadEndAnnouncement(direction: 1 | -1): string {
  return direction === 1
    ? "No position after this one."
    : "No position before this one.";
}

export function formatCanvasDeadEndAnnouncement(): string {
  return "No other zone can receive this widget.";
}

export function formatDropAnnouncement(
  widgetName: string,
  zoneName: string,
): string {
  return `Moved ${widgetName} to ${zoneName}.`;
}

export function formatCancelAnnouncement(widgetName: string): string {
  return `Move cancelled. ${widgetName} stays where it was.`;
}

export function formatCannotMoveAnnouncement(widgetName: string): string {
  return `${widgetName} cannot be moved.`;
}

/** Voiced when another modality takes over (pointer handover, external end). */
export function formatKeyboardMoveEndedAnnouncement(): string {
  return "Keyboard move ended.";
}
